import React, { useMemo, useState } from 'react';

import {
  AddIcon,
  Button,
  DeleteIcon,
  Divider,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
  type TypeSelectOption,
  dialogService,
} from '@thefarmersfront/kitchen';

import type { GetRowIdParams, IRowNode } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useShallow } from 'zustand/react/shallow';

import { Bullet, FormField, Table } from '~/components/common';

import { useGetFilterAllQuery, useGetRequestGoodsDownloadTemplateMutation } from '~/hooks/receive';
import { useReceiveStore, useUserInfoStore } from '~/store';
import { formatNumber, getReceiveRegisterModalColumnDefs } from '~/utils';
import { getMatchingXDockCenter } from '~/utils/receive';

import { DEFAULT_INPUT_STATUS, MAX_RECEIVE_REQUEST_PRODUCT_COUNT, MAX_RECEIVE_REQUEST_SKU_COUNT } from '~/constants';

import { ReceiveRequestAddGoodsModal } from '../ReceiveRequestAddGoodsModal';
import { ReceiveRequestExcelUploadFailModal } from '../ReceiveRequestExcelUploadFailModal';
import { ReceiveRequestExcelUploadModal } from '../ReceiveRequestExcelUploadModal';

import type {
  TypeAddGoodsModalGoods,
  TypePostRequestGoodsUploadResponse,
  TypeReceiveRequestItemErrorState,
  TypeReceiveRequestRegisterModalGoods,
} from '~/types/receive.types';

import styles from './ReceiveRegisterFirstStepContent.module.scss';

const CENTER_CC03 = 'CC03';
const CENTER_CC04 = 'CC04';

type TypeReceiveRegisterFirstStepContentProps = {
  ownerCode: string;
  currentTemperatureOption: TypeSelectOption<string>[];
  setCurrentTemperatureOption: React.Dispatch<React.SetStateAction<TypeSelectOption<string>[]>>;
  gridRef: React.MutableRefObject<AgGridReact<TypeReceiveRequestRegisterModalGoods> | null>;
  rowErrors: TypeReceiveRequestItemErrorState[];
  setRowErrors: (rowErrors: TypeReceiveRequestItemErrorState[]) => void;
};

/**
 * [입고관리][입고요청관리] 입고요청등록 모달 > 기본 정보/입고 상품 입력 단계 (STEP 1) 컴포넌트
 */
const ReceiveRegisterFirstStepContent = ({
  ownerCode,
  currentTemperatureOption,
  setCurrentTemperatureOption,
  gridRef,
  rowErrors,
  setRowErrors,
}: TypeReceiveRegisterFirstStepContentProps) => {
  const downloadTemplateMutation = useGetRequestGoodsDownloadTemplateMutation();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const { data: filterOptions } = useGetFilterAllQuery('ADD_RECEIVING_GOODS');

  const [
    requestModalDefaultInfoValue,
    setRequestModalDefaultInfoValue,
    requestModalDefaultInfoStatus,
    setRequestModalDefaultInfoStatus,
    requestModalGoodsList,
    setRequestModalGoodsList,
  ] = useReceiveStore(
    useShallow((state) => [
      state.requestModalDefaultInfoValue,
      state.setRequestModalDefaultInfoValue,
      state.requestModalDefaultInfoStatus,
      state.setRequestModalDefaultInfoStatus,
      state.requestModalGoodsList,
      state.setRequestModalGoodsList,
    ])
  );

  const { columnDefs } = getReceiveRegisterModalColumnDefs({
    hasPermissionNFA,
    data: requestModalGoodsList,
    setData: setRequestModalGoodsList,
  });

  const totalSkuCount = new Set(requestModalGoodsList.map(({ goodsCode }) => goodsCode)).size;
  const totalQtyCount = requestModalGoodsList.reduce((acc, { totalQty }) => acc + (totalQty ?? 0), 0);

  const [isExcelUploadModalOpen, setIsExcelUploadModalOpen] = useState(false);
  const [isExcelUploadFailModalOpen, setIsExcelUploadFailModalOpen] = useState(false);
  const [isRequestAddGoodsModalOpen, setIsRequestAddGoodsModalOpen] = useState(false);

  const [excelUploadedData, setExcelUploadedData] = useState<TypePostRequestGoodsUploadResponse | null>(null);
  const [selectedGoodsRow, setSelectedGoodsRow] = useState<IRowNode<TypeReceiveRequestRegisterModalGoods>[]>([]);

  const handleClickExcelTemplateDownload = () => {
    downloadTemplateMutation.mutate(ownerCode);
  };

  const handleClickGoodsDelete = () => {
    const selectedRowIndexes = new Set(selectedGoodsRow.map((row) => row.rowIndex));

    setRequestModalGoodsList(requestModalGoodsList.filter((_, index) => !selectedRowIndexes.has(index)));
    setRowErrors(rowErrors.filter((_, index) => !selectedRowIndexes.has(index)));
  };

  const handleClickUploadExcel = () => {
    setIsExcelUploadModalOpen(true);
  };

  const handleClickGoodsAdd = () => {
    setIsRequestAddGoodsModalOpen(true);
  };

  const handleChangeRowSelection = (rows: IRowNode[]) => {
    setSelectedGoodsRow(rows);
  };

  const handleChangeCenter = (_: unknown, option: TypeSelectOption<string>) => {
    if (requestModalGoodsList.length > 0) {
      dialogService.addDialog({
        children:
          '입고 상품이 추가된 상태에서는 입고 센터를 변경할 수 없습니다. 입고 상품을 전체 삭제 후 다시 시도해주세요.',
      });

      return;
    }

    setRequestModalDefaultInfoValue({
      center: option,
      temperature: null,
      xDock: 'false',
    });

    setCurrentTemperatureOption(filterOptions?.groupedTemperatureByCenter[option.value] ?? []);

    setRequestModalDefaultInfoStatus({
      ...requestModalDefaultInfoStatus,
      center: DEFAULT_INPUT_STATUS,
    });
  };

  const handleChangeTemperature = (_: unknown, option: TypeSelectOption<string>) => {
    if (requestModalGoodsList.length > 0) {
      dialogService.addDialog({
        children:
          '입고 상품이 추가된 상태에서는 입고 센터를 변경할 수 없습니다. 입고 상품을 전체 삭제 후 다시 시도해주세요.',
      });

      return;
    }

    /** '입고센터' 선택값이 김포(CC02)/창원(CC04)일 때, 동일한 온도대의 평택(CC03) 온도대 지정 */
    const isXDock =
      requestModalDefaultInfoValue.center?.value === 'CC02' ||
      requestModalDefaultInfoValue.center?.value === CENTER_CC04;

    setRequestModalDefaultInfoValue({
      temperature: option,
      ...(isXDock && {
        xDockCenter: getMatchingXDockCenter(filterOptions?.groupedTemperatureByCenter[CENTER_CC03] ?? [], option.value),
      }),
    });

    setRequestModalDefaultInfoStatus({
      ...requestModalDefaultInfoStatus,
      temperature: DEFAULT_INPUT_STATUS,
    });
  };

  const handleChangeXDock = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value as 'true' | 'false';

    setRequestModalDefaultInfoValue({
      xDock: newValue,
      ...(newValue === 'true' && {
        xDockCenter: getMatchingXDockCenter(
          filterOptions?.groupedTemperatureByCenter[CENTER_CC03] ?? [],
          requestModalDefaultInfoValue.temperature?.value ?? null
        ),
      }),
    });
  };

  const handleChangeSupplier = (_: unknown, option: TypeSelectOption<string>) => {
    if (requestModalGoodsList.length > 0) {
      dialogService.addDialog({
        children:
          '입고 상품이 추가된 상태에서는 공급사를 변경할 수 없습니다. 입고 상품을 전체 삭제 후 다시 시도해주세요.',
      });

      return;
    }

    setRequestModalDefaultInfoValue({
      supplierCode: option,
    });

    setRequestModalDefaultInfoStatus({
      ...requestModalDefaultInfoStatus,
      supplierCode: DEFAULT_INPUT_STATUS,
    });
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setRequestModalDefaultInfoValue({
      [e.target.id]: e.target.value,
    });
  };

  const handleCancelExcelUploadModal = () => {
    setIsExcelUploadModalOpen(false);
  };

  const mergeGoodsByGoodsCode = (
    registeredGoodsArr: TypeReceiveRequestRegisterModalGoods[],
    newGoodsArr: TypeReceiveRequestRegisterModalGoods[]
  ) => {
    /**
     * 정렬 기준
     * 1. 기본적으로, 추가되는 아이템들의 행 번호(row) 순서 유지
     * 2. 새롭게 추가되는 상품코드명이 기존에 없었다면 목록 최상위 정렬 / 기존 목록에 존재한다면 해당 상품코드들 중 최상위 정렬
     */
    const existingGoodsCodes = new Set(registeredGoodsArr.map((item) => item.goodsCode));
    const groupedNewGoodsByExistingCode: {
      [key: string]: TypeReceiveRequestRegisterModalGoods[];
    } = {};

    // 새로 추가되는 아이템들이 기존에 존재하는 상품코드였다면 goodsCode 별로 그룹화
    newGoodsArr.forEach((newItem) => {
      if (existingGoodsCodes.has(newItem.goodsCode)) {
        if (!groupedNewGoodsByExistingCode[newItem.goodsCode]) {
          groupedNewGoodsByExistingCode[newItem.goodsCode] = [];
        }
        groupedNewGoodsByExistingCode[newItem.goodsCode].push(newItem);
      }
    });

    // 기존 배열에서 해당 goodsCode가 있는 위치에 새 아이템들을 삽입
    const existingGoodsCodeItems = registeredGoodsArr.reduce<TypeReceiveRequestRegisterModalGoods[]>(
      (acc, registeredGoods) => {
        if (groupedNewGoodsByExistingCode[registeredGoods.goodsCode]) {
          acc.push(...groupedNewGoodsByExistingCode[registeredGoods.goodsCode]);

          delete groupedNewGoodsByExistingCode[registeredGoods.goodsCode];
        }
        acc.push(registeredGoods);
        return acc;
      },
      []
    );

    // 새 상품 중 기존 목록에 없는 상품들을 별도로 추가
    const newGoodsCodeItems = newGoodsArr.filter((item) => !existingGoodsCodes.has(item.goodsCode));

    const mergedGoods = [...newGoodsCodeItems, ...existingGoodsCodeItems];
    const indexedGoods = mergedGoods.map((item, index) => ({
      ...item,
      index: index + 1,
    }));

    return indexedGoods;
  };

  const handleConfirmExcelUploadModal = (data: TypePostRequestGoodsUploadResponse) => {
    const formattedData = data.successItems.map((item) => {
      const isDateExist = !!(item.expirationDate || item.manufactureDate);
      const dateType = item.expirationDate ? 'expirationDate' : 'manufactureDate';
      const date = item[dateType];

      return {
        ...item,
        index: 0,
        dateUseYn: isDateExist,
        dateType: isDateExist ? dateType : null,
        date: isDateExist ? date : null,
        lotNo: item.lotNo ?? '',
        totalQty: item.boxQty * item.boxPerQty + item.eachQty,
      };
    });

    const updatedGoods = mergeGoodsByGoodsCode(requestModalGoodsList, formattedData);

    setRequestModalGoodsList(updatedGoods);
    setIsExcelUploadModalOpen(false);

    if (data.failCount > 0) {
      setExcelUploadedData(data);
      setIsExcelUploadFailModalOpen(true);
    }
  };

  const handleConfirmExcelUploadFailModal = () => {
    setIsExcelUploadFailModalOpen(false);
  };

  const handleCancelRequestAddModal = () => {
    setIsRequestAddGoodsModalOpen(false);
  };

  const handleConfirmRequestAddModal = (data: TypeAddGoodsModalGoods[]) => {
    const formattedData = data.map((item) => ({
      /** 모달 선택 정보 */
      goodsId: item.goodsId,
      goodsCode: item.goodsCode,
      nsid: item.nsid ?? '',
      alternativeCode: item.alternativeCode,
      goodsName: item.goodsName,
      kurlyKeepTypeDesc: item.kurlyKeepTypeDesc,
      boxPerQty: item.boxPerQty,
      /** 초기화 */
      index: 0,
      dateUseYn: true,
      dateType: 'expirationDate',
      date: null,
      palletYn: false,
      lotNo: '',
      boxQty: 0,
      eachQty: 0,
      totalQty: 0,
    }));

    const updatedGoods = mergeGoodsByGoodsCode(requestModalGoodsList, formattedData);

    setRequestModalGoodsList(updatedGoods);
    setIsRequestAddGoodsModalOpen(false);
  };

  const isXDockAllowed = useMemo(() => {
    return (
      !hasPermission3PL &&
      (requestModalDefaultInfoValue.center?.value === 'CC02' ||
        requestModalDefaultInfoValue.center?.value === 'CC04') &&
      requestModalDefaultInfoValue.temperature !== null
    );
  }, [requestModalDefaultInfoValue.center?.value, requestModalDefaultInfoValue.temperature, hasPermission3PL]);

  return (
    <>
      <div className={styles['default-info-section']}>
        <Text size="xxlarge" weight="bold">
          기본 정보
        </Text>
        <div>
          <FormField
            label="입고 센터"
            required
            contents={
              <>
                <Select
                  size="small"
                  placeholder="선택"
                  status={requestModalDefaultInfoStatus.center.status}
                  validationMessage={requestModalDefaultInfoStatus.center.message}
                  options={filterOptions?.center ?? []}
                  value={requestModalDefaultInfoValue.center}
                  onChange={handleChangeCenter}
                />
                <Select
                  size="small"
                  placeholder="선택"
                  disabled={requestModalDefaultInfoValue.center === null}
                  status={requestModalDefaultInfoStatus.temperature.status}
                  validationMessage={requestModalDefaultInfoStatus.temperature.message}
                  options={currentTemperatureOption}
                  value={requestModalDefaultInfoValue.temperature}
                  onChange={handleChangeTemperature}
                />
              </>
            }
            contentsClassName={styles['content-inputs']}
          />
          <FormField
            label="입고대행서비스"
            required
            contents={
              <RadioGroup
                className={styles['content-radio']}
                name="xdock"
                size="medium"
                disabled={!isXDockAllowed}
                status={requestModalDefaultInfoStatus.xDock.status}
                validationMessage={requestModalDefaultInfoStatus.xDock.message}
                value={requestModalDefaultInfoValue.xDock}
                onChange={handleChangeXDock}
              >
                <Radio value="true" label="이용함" />
                <Radio value="false" label="이용안함" />
              </RadioGroup>
            }
          />
          {requestModalDefaultInfoValue.xDock === 'true' && (
            <FormField
              label="경유센터"
              required
              contents={
                <Select
                  className={styles['content-input']}
                  size="small"
                  placeholder="선택"
                  maxWidth="229px"
                  disabled
                  options={[]}
                  value={requestModalDefaultInfoValue.xDockCenter}
                />
              }
            />
          )}
        </div>
        <Divider color="gray100" />
        <div className={styles['text-area-section']}>
          <FormField
            label="공급사"
            required
            contents={
              <Select
                className={styles['content-input']}
                size="small"
                placeholder="선택"
                maxWidth="229px"
                status={requestModalDefaultInfoStatus.supplierCode.status}
                validationMessage={requestModalDefaultInfoStatus.supplierCode.message}
                options={filterOptions?.supplier ?? []}
                value={requestModalDefaultInfoValue.supplierCode}
                onChange={handleChangeSupplier}
              />
            }
          />
          <FormField
            label="비고"
            contents={
              <Textarea
                id="note"
                className={styles['content-input']}
                size="small"
                maxWidth="503px"
                height="78px"
                placeholder="요청 또는 특이사항 입력"
                count
                maxLength={255}
                value={requestModalDefaultInfoValue.note}
                onChange={handleChangeInput}
              />
            }
          />
        </div>
      </div>
      <div className={styles['table-section']}>
        <div className={styles['table-top-field']}>
          <div className={styles['table-top-left-field']}>
            <Text className={styles['title']} size="xxlarge" weight="bold">
              입고 상품
            </Text>
            <Text size="medium" weight="semi-bold" color="tertiary">
              입고 가능 품목 {formatNumber(totalSkuCount)}/{formatNumber(MAX_RECEIVE_REQUEST_SKU_COUNT)}
            </Text>
            <Bullet fill="#848F9A" />
            <Text size="medium" weight="semi-bold" color="tertiary">
              입고 가능 수량 {formatNumber(totalQtyCount)}/{formatNumber(MAX_RECEIVE_REQUEST_PRODUCT_COUNT)}
            </Text>
          </div>
          <div className={styles['table-top-right-field']}>
            <Button size="small" variant="outlined" color="tertiary" onClick={handleClickExcelTemplateDownload}>
              엑셀 양식 다운로드
            </Button>
            <Divider orientation="vertical" color="gray100" />
            <Button
              size="small"
              variant="outlined"
              color="tertiary"
              leftIcon={<DeleteIcon size={16} />}
              disabled={selectedGoodsRow.length === 0}
              onClick={handleClickGoodsDelete}
            >
              삭제
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="tertiary"
              disabled={
                requestModalDefaultInfoValue.temperature === null || requestModalDefaultInfoValue.supplierCode === null
              }
              onClick={handleClickUploadExcel}
            >
              엑셀 추가
            </Button>
            <Button
              size="small"
              color="secondary"
              disabled={
                requestModalDefaultInfoValue.temperature === null || requestModalDefaultInfoValue.supplierCode === null
              }
              leftIcon={<AddIcon size={16} />}
              onClick={handleClickGoodsAdd}
            >
              추가
            </Button>
          </div>
        </div>
        <div className={styles['table-wrapper']}>
          <Table
            gridRef={gridRef}
            className={styles['table']}
            data={requestModalGoodsList}
            options={{
              columnDefs,
              suppressScrollOnNewData: true,
              suppressRowVirtualisation: false,
              getRowId: (params: GetRowIdParams<TypeReceiveRequestRegisterModalGoods>) => String(params.data.index),
            }}
            rowStatusType="RECEIVE_REGISTER"
            onSelectionChange={handleChangeRowSelection}
          />
        </div>
      </div>
      {isExcelUploadModalOpen && (
        <ReceiveRequestExcelUploadModal
          ownerCode={ownerCode}
          center={requestModalDefaultInfoValue.center?.value as string}
          temperature={requestModalDefaultInfoValue.temperature?.value as string}
          supplierCode={requestModalDefaultInfoValue.supplierCode?.value as string}
          open={isExcelUploadModalOpen}
          onCancel={handleCancelExcelUploadModal}
          onConfirm={handleConfirmExcelUploadModal}
        />
      )}
      {isExcelUploadFailModalOpen && excelUploadedData && (
        <ReceiveRequestExcelUploadFailModal
          data={excelUploadedData}
          open={isExcelUploadFailModalOpen}
          onConfirm={handleConfirmExcelUploadFailModal}
        />
      )}
      {isRequestAddGoodsModalOpen && (
        <ReceiveRequestAddGoodsModal
          ownerCode={ownerCode}
          temperature={requestModalDefaultInfoValue.temperature?.value as string}
          supplierCode={requestModalDefaultInfoValue.supplierCode?.value as string}
          open={isRequestAddGoodsModalOpen}
          onCancel={handleCancelRequestAddModal}
          onConfirm={handleConfirmRequestAddModal}
        />
      )}
    </>
  );
};

export default ReceiveRegisterFirstStepContent;
